/** Infrastructure and releases: read-only inventory, trends, the release and the freeze switch (U076-U078). */
import { decodeInfraRow, type InfraRow } from "../../contracts/ops.ts";
import { badge, definitions, instantCell, metric } from "../../app/components.ts";
import { h, type Child } from "../../app/dom.ts";
import { formatBasisPoints, formatDurationUs } from "../../app/format.ts";
import type { ViewContext, ViewHandle } from "../../app/view.ts";
import { byKindOf, mountOps, panel, shortHash, simpleTable } from "../ops-common.ts";

type Release = Extract<InfraRow, { kind: "release" }>;

const probeTone = (probe: "PASSING" | "FAILING" | "UNKNOWN"): "ok" | "bad" | "info" =>
  probe === "PASSING" ? "ok" : probe === "FAILING" ? "bad" : "info";
const checkTone = (result: "PASSED" | "FAILED" | "NOT_RUN"): "ok" | "bad" | "info" =>
  result === "PASSED" ? "ok" : result === "FAILED" ? "bad" : "info";

function trend(bpPerHour: number): string {
  return `${bpPerHour < 0 ? "falling" : bpPerHour === 0 ? "flat" : "rising"} ${formatBasisPoints(Math.abs(bpPerHour))} per hour`;
}

function releasePanel(row: Release): HTMLElement {
  const generationsAgree = row.installedGeneration === row.acceptedGeneration;
  return panel(
    `Release ${row.releaseId}`,
    "infra-release",
    definitions([
      ["Commit", shortHash(row.commit)],
      ["GitHub run", row.githubRun],
      ["Accepted generation", row.acceptedGeneration],
      [
        "Installed generation",
        h(
          "span",
          { attrs: { "data-generation": generationsAgree ? "agree" : "differs" } },
          `${row.installedGeneration} `,
          generationsAgree
            ? badge("matches the accepted release", "ok")
            : badge("differs from the accepted release", "bad"),
        ),
      ],
      ["Host check", badge(row.hostCheck, checkTone(row.hostCheck))],
      ["External check", badge(row.externalCheck, checkTone(row.externalCheck))],
      ["Schema compatible", row.schemaCompatible ? "yes" : "no"],
      ["Deployment phase", row.phase],
      [
        "Maintenance window",
        row.maintenanceStart === null
          ? metric(null)
          : h(
              "span",
              {},
              instantCell(row.maintenanceStart),
              " to ",
              instantCell(row.maintenanceEnd),
            ),
      ],
      ["Rollback eligible", row.rollbackEligible ? "yes" : "no"],
      ["Previous recoverable release", metric(row.previousRelease)],
      [
        "Trading rearm",
        row.tradingRearmed
          ? "rearmed"
          : "not rearmed: deployment acceptance does not rearm trading",
      ],
    ]),
  );
}

/** Draw the inventory, the trends, the release and the freeze switch. */
export function drawInfra(rows: readonly InfraRow[]): readonly Child[] {
  return [
    h(
      "p",
      { class: "note" },
      "Everything here is read-only: no shell, no deploy and no restart control is offered.",
    ),
    panel(
      "Services and nodes",
      "infra-services",
      simpleTable(
        "Services",
        [
          "Service",
          "Node",
          "Intended",
          "Running",
          "Replica owner",
          "Restarts",
          "Liveness",
          "Readiness",
        ],
        byKindOf(rows, "service").map((row) => [
          row.service,
          row.node,
          row.intendedVersion,
          h(
            "span",
            {
              attrs: {
                "data-version": row.runningVersion === row.intendedVersion ? "match" : "differs",
              },
            },
            row.runningVersion === row.intendedVersion
              ? row.runningVersion
              : `${row.runningVersion} (differs from intended)`,
          ),
          row.replicaOwner,
          String(row.restartCount),
          badge(row.liveness, probeTone(row.liveness)),
          badge(row.readiness, probeTone(row.readiness)),
        ]),
      ),
    ),
    panel(
      "Resource trends",
      "infra-resources",
      simpleTable(
        "Resource trends",
        ["Subject", "Resource", "Use", "Trend", "Measured", "Telemetry"],
        byKindOf(rows, "resource").map((row) => [
          row.service,
          row.resource,
          row.freshness === "UNAVAILABLE" ? metric(null) : formatBasisPoints(row.usageBp),
          row.freshness === "UNAVAILABLE" ? metric(null) : trend(row.trendBpPerHour),
          instantCell(row.measuredAt),
          row.freshness === "FRESH"
            ? badge("fresh", "ok")
            : badge(
                row.ageUs === null ? "unavailable" : `stale, ${formatDurationUs(row.ageUs)} old`,
                "warn",
              ),
        ]),
      ),
    ),
    ...byKindOf(rows, "release").map(releasePanel),
    panel(
      "Release freeze",
      "infra-freeze",
      ...byKindOf(rows, "freeze").map((row) =>
        h(
          "p",
          { attrs: { "data-freeze": row.engaged ? "engaged" : "released" } },
          row.engaged ? badge("Engaged", "warn") : badge("Released", "ok"),
          row.engaged ? " since " : " (read-only here)",
          row.engaged ? instantCell(row.since) : null,
          row.reason === null ? null : `: ${row.reason}`,
        ),
      ),
    ),
  ];
}

/** Mount the infrastructure view. */
export function mountInfra(ctx: ViewContext): ViewHandle {
  return mountOps(ctx, {
    id: "infra",
    title: "Infrastructure and releases",
    projection: "infra",
    row: decodeInfraRow,
    draw: (rows) => drawInfra(rows),
  });
}
